/**
 * 606 MT5 Manual Trading — Multi-Account Unified Orders.
 *
 * This page owns STATE + API ACTIONS + top-level layout only.
 * Presentational pieces live under ./manual-trading/:
 *   OrderForm, AccountsPane, PositionsPane, PendingPane, TemplatesPane,
 *   ManualActionBar, TemplateChips, LastResultsCard, ManualTradingDialogs,
 *   useTradeSounds (sound alerts + closed-position detection).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus, TrendingUp, TrendingDown, DollarSign, ListChecks,
  Edit3, Target, Layers, Bookmark,
} from 'lucide-react';

import DashboardHeader from '../components/DashboardHeader';
import PageBreadcrumb from '../components/PageBreadcrumb';
import { useLanguage } from '../context/LanguageContext';
import { getAuthHeaders } from '../utils/authHeaders';

import { StatCard, TabbedSection } from './manual-trading/OrderFormBits';
import OrderForm from './manual-trading/OrderForm';
import AccountsPane from './manual-trading/AccountsPane';
import PositionsPane from './manual-trading/PositionsPane';
import PendingPane from './manual-trading/PendingPane';
import TemplatesPane from './manual-trading/TemplatesPane';
import ManualActionBar from './manual-trading/ManualActionBar';
import TemplateChips from './manual-trading/TemplateChips';
import LastResultsCard from './manual-trading/LastResultsCard';
import {
  AddAccountDialog, DeleteAccountDialog, TpslModifyDialog,
  CancelAllPendingDialog, ExecuteTemplateDialog, TemplateEditorDialog,
} from './manual-trading/ManualTradingDialogs';
import { useTradeSounds } from './manual-trading/useTradeSounds';
import { fmtMoney } from './manual-trading/tradingHelpers';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api/trading/mt5/manual`;

export default function Meta5ManualTrading() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const isAr = language === 'ar';

  // ─── State ─────────────────────────────────────────────
  const [accounts, setAccounts] = useState([]);      // status[] from GET /status
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  // Add account dialog
  const [addOpen, setAddOpen] = useState(false);
  const [newAcc, setNewAcc] = useState({ label: '', login: '', password: '', server: '', mode: 'demo' });
  const [saving, setSaving] = useState(false);

  // Delete confirm
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Order form
  const [order, setOrder] = useState({
    mode: 'market',           // 'market' | 'pending'
    orderType: 'buy_limit',   // used when mode === 'pending'
    symbol: 'XAUUSD',
    volume: '0.01',
    entryPrice: '',
    // SL / TP can be entered as either absolute price or a $-distance.
    slMode: 'price',          // 'price' | 'distance'
    tpMode: 'price',          // 'price' | 'distance'
    sl: '',
    tp: '',
  });
  const [placing, setPlacing] = useState(false);
  const [lastResults, setLastResults] = useState(null);
  // Live bid/ask for the currently selected symbol.
  const [livePrice, setLivePrice] = useState(null);   // { bid, ask } | null

  // Close-all instant action (no confirmation dialog — the user wants speed).
  const [closingAll, setClosingAll] = useState(false);

  // TP/SL modify dialog
  const [tpslOpen, setTpslOpen] = useState(false);
  const [tpslForm, setTpslForm] = useState({
    symbol: 'XAUUSD',
    slMode: 'price', tpMode: 'price',
    sl: '', tp: '',
  });
  const [modifyingTpsl, setModifyingTpsl] = useState(false);

  // Active sub-tab of the combined data box (accounts | positions | pending | templates)
  const [subTab, setSubTab] = useState('accounts');

  // Positions & pending orders (polled in the background, faster when the tab is open)
  const [positionsData, setPositionsData] = useState({ accounts: [], total_positions: 0 });
  const [pendingData, setPendingData] = useState({ accounts: [], total_orders: 0 });
  const [closingSinglePos, setClosingSinglePos] = useState(null);   // position_id
  const [cancelingSingle, setCancelingSingle] = useState(null);     // order broker_id

  // Templates
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [tplOpen, setTplOpen] = useState(false);
  const [editingTpl, setEditingTpl] = useState(null);
  const [tplForm, setTplForm] = useState({
    name: '', symbol: 'XAUUSD', mode: 'market', side: 'buy',
    orderType: 'buy_limit', volume: '0.01', entryPrice: '', sl: '', tp: '',
    color: '#d4af37',
  });
  const [savingTpl, setSavingTpl] = useState(false);
  const [executingTpl, setExecutingTpl] = useState(null);   // tpl id
  const [confirmExecuteTpl, setConfirmExecuteTpl] = useState(null);

  // Cancel-all-pending confirm
  const [confirmCancelPending, setConfirmCancelPending] = useState(false);
  const [cancelingAll, setCancelingAll] = useState(false);

  // Persist per-account connect errors (until the next connect attempt)
  const [connectErrors, setConnectErrors] = useState([]);

  // Sound alerts + closed-position diff detection
  const { soundEnabled, toggleSound, knownPositionsRef, trackAndNotify } = useTradeSounds(isAr);

  // ─── Data loaders ──────────────────────────────────────
  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/status`, { headers: getAuthHeaders() });
      setAccounts(res.data?.accounts || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || (isAr ? 'فشل تحميل الحسابات' : 'Load failed'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [isAr]);

  useEffect(() => { load(); }, [load]);

  const refresh = () => { setRefreshing(true); load(); };

  const loadPositions = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/positions`, { headers: getAuthHeaders() });
      const next = res.data || { accounts: [], total_positions: 0 };
      trackAndNotify(next);   // toast + chime for any position closed since last poll
      setPositionsData(next);
    } catch {
      /* silent — polling */
    }
  }, [trackAndNotify]);

  const loadPending = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/pending-orders`, { headers: getAuthHeaders() });
      setPendingData(res.data || { accounts: [], total_orders: 0 });
    } catch {
      /* silent */
    }
  }, []);

  const loadTemplates = useCallback(async () => {
    setTemplatesLoading(true);
    try {
      const res = await axios.get(`${API}/templates`, { headers: getAuthHeaders() });
      setTemplates(res.data?.templates || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setTemplatesLoading(false);
    }
  }, []);

  // Load templates once — they're used both in the tab and as chips near the order form
  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  // Poll positions + pending in the BACKGROUND (every 8s) so tab badges are
  // always fresh; speed up to 5s while the user is ON the tab.
  useEffect(() => {
    const interval = (subTab === 'positions' || subTab === 'pending') ? 5000 : 8000;
    loadPositions();
    loadPending();
    const iv = setInterval(() => {
      loadPositions();
      loadPending();
    }, interval);
    return () => clearInterval(iv);
  }, [subTab, loadPositions, loadPending]);

  // ─── Positions actions ────────────────────────────────
  const closeSinglePosition = async (accountId, positionId) => {
    setClosingSinglePos(positionId);
    // Remove from the diff-tracker so the poll doesn't fire a duplicate chime.
    knownPositionsRef.current.delete(`${accountId}-${positionId}`);

    // Optimistic UI — strip the row instantly; poll re-instates it on failure.
    setPositionsData((prev) => {
      const nextAccounts = (prev.accounts || []).map((acc) => {
        if (acc.account_id !== accountId) return acc;
        return { ...acc, positions: (acc.positions || []).filter((p) => p.broker_id !== positionId) };
      });
      return {
        ...prev,
        accounts: nextAccounts,
        total_positions: (prev.total_positions || 0) > 0 ? prev.total_positions - 1 : 0,
      };
    });

    const toastId = toast.loading(isAr ? 'جاري إغلاق الصفقة...' : 'Closing position...');
    try {
      await axios.post(
        `${API}/positions/${accountId}/${positionId}/close`,
        {}, { headers: getAuthHeaders(), timeout: 15000 },
      );
      toast.success(isAr ? 'تم إغلاق الصفقة' : 'Position closed', { id: toastId });
      setTimeout(() => { loadPositions(); load(); }, 250);
      setTimeout(() => { loadPositions(); }, 900);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed', { id: toastId });
      setTimeout(loadPositions, 300);
    } finally {
      setClosingSinglePos(null);
    }
  };

  // ─── Pending actions ──────────────────────────────────
  const cancelSinglePending = async (accountId, orderId) => {
    setCancelingSingle(orderId);

    // Optimistic UI: strip the order immediately.
    setPendingData((prev) => {
      const nextAccounts = (prev.accounts || []).map((acc) => {
        if (acc.account_id !== accountId) return acc;
        return { ...acc, orders: (acc.orders || []).filter((o) => o.broker_id !== orderId) };
      });
      return {
        ...prev, accounts: nextAccounts,
        total_pending: (prev.total_pending || 0) > 0 ? prev.total_pending - 1 : 0,
      };
    });

    const toastId = toast.loading(isAr ? 'جاري الإلغاء...' : 'Cancelling...');
    try {
      const acc = pendingData.accounts.find((a) => a.account_id === accountId);
      const order = acc?.orders?.find((o) => o.broker_id === orderId);
      const res = await axios.post(
        `${API}/cancel-pending`,
        { account_ids: [accountId], symbol: order?.symbol },
        { headers: getAuthHeaders(), timeout: 15000 },
      );
      toast.success(
        isAr
          ? `تم إلغاء ${res.data?.total_cancelled || 0} أمر معلّق`
          : `Cancelled ${res.data?.total_cancelled || 0} pending order(s)`,
        { id: toastId },
      );
      setTimeout(loadPending, 250);
      setTimeout(loadPending, 900);
    } catch (err) {
      const msg = err.code === 'ECONNABORTED'
        ? (isAr ? 'انتهت مهلة الطلب — أعد المحاولة' : 'Request timed out — please retry')
        : (err.response?.data?.detail || 'Failed');
      toast.error(msg, { id: toastId });
      setTimeout(loadPending, 300);
    } finally {
      setCancelingSingle(null);
    }
  };

  const cancelAllPending = async () => {
    setConfirmCancelPending(false);
    setCancelingAll(true);

    // Optimistic UI: clear all pending immediately.
    setPendingData((prev) => ({
      ...prev,
      accounts: (prev.accounts || []).map((a) => ({ ...a, orders: [] })),
      total_pending: 0,
    }));

    const toastId = toast.loading(isAr ? 'جاري إلغاء الكل...' : 'Cancelling all...');
    try {
      const res = await axios.post(
        `${API}/cancel-pending`,
        {},
        { headers: getAuthHeaders(), timeout: 20000 },
      );
      toast.success(
        isAr
          ? `تم إلغاء ${res.data?.total_cancelled || 0} أمر معلّق`
          : `Cancelled ${res.data?.total_cancelled || 0} pending order(s)`,
        { id: toastId },
      );
      setTimeout(loadPending, 300);
      setTimeout(loadPending, 1000);
    } catch (err) {
      const msg = err.code === 'ECONNABORTED'
        ? (isAr ? 'انتهت مهلة الطلب — أعد المحاولة' : 'Request timed out — please retry')
        : (err.response?.data?.detail || 'Failed');
      toast.error(msg, { id: toastId });
      setTimeout(loadPending, 400);
    } finally {
      setCancelingAll(false);
    }
  };

  // ─── Template actions ─────────────────────────────────
  const openTplDialog = (tpl = null) => {
    if (tpl) {
      setEditingTpl(tpl);
      setTplForm({
        name: tpl.name,
        symbol: tpl.symbol,
        mode: tpl.mode,
        side: tpl.side || 'buy',
        orderType: tpl.order_type || 'buy_limit',
        volume: String(tpl.volume),
        entryPrice: tpl.entry_price != null ? String(tpl.entry_price) : '',
        sl: tpl.sl != null ? String(tpl.sl) : '',
        tp: tpl.tp != null ? String(tpl.tp) : '',
        color: tpl.color || '#d4af37',
      });
    } else {
      setEditingTpl(null);
      setTplForm({
        name: '', symbol: order.symbol, mode: order.mode,
        side: 'buy', orderType: order.orderType || 'buy_limit',
        volume: order.volume, entryPrice: order.entryPrice,
        sl: order.sl, tp: order.tp, color: '#d4af37',
      });
    }
    setTplOpen(true);
  };

  const saveTemplate = async () => {
    if (!tplForm.name.trim()) {
      toast.error(isAr ? 'أدخل اسم القالب' : 'Name required');
      return;
    }
    const body = {
      name: tplForm.name.trim(),
      symbol: (tplForm.symbol || '').trim().toUpperCase(),
      mode: tplForm.mode,
      volume: Number(tplForm.volume),
      color: tplForm.color,
    };
    if (tplForm.mode === 'market') {
      body.side = tplForm.side;
    } else {
      body.order_type = tplForm.orderType;
      body.entry_price = Number(tplForm.entryPrice);
      if (!body.entry_price || body.entry_price <= 0) {
        toast.error(isAr ? 'أدخل سعر الدخول' : 'Entry price required');
        return;
      }
    }
    if (tplForm.sl && Number(tplForm.sl) > 0) body.sl = Number(tplForm.sl);
    if (tplForm.tp && Number(tplForm.tp) > 0) body.tp = Number(tplForm.tp);

    setSavingTpl(true);
    try {
      if (editingTpl) {
        await axios.put(`${API}/templates/${editingTpl.id}`, body, { headers: getAuthHeaders() });
        toast.success(isAr ? 'تم التحديث' : 'Updated');
      } else {
        await axios.post(`${API}/templates`, body, { headers: getAuthHeaders() });
        toast.success(isAr ? 'تم الحفظ' : 'Saved');
      }
      setTplOpen(false);
      loadTemplates();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setSavingTpl(false);
    }
  };

  const deleteTemplate = async (tplId) => {
    try {
      await axios.delete(`${API}/templates/${tplId}`, { headers: getAuthHeaders() });
      toast.success(isAr ? 'تم الحذف' : 'Deleted');
      loadTemplates();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const executeTemplate = async (tpl) => {
    setConfirmExecuteTpl(null);
    setExecutingTpl(tpl.id);
    setLastResults(null);
    try {
      const res = await axios.post(
        `${API}/templates/${tpl.id}/execute`, {}, { headers: getAuthHeaders() },
      );
      setLastResults({ ...res.data, mode: tpl.mode });
      const successes = (res.data?.results || []).filter((r) => r.success).length;
      const failures = (res.data?.results || []).filter((r) => !r.success).length;
      if (failures > 0 && successes === 0) {
        toast.error(isAr ? `فشلت كل الأوامر (${failures})` : `All failed (${failures})`);
      } else if (failures > 0) {
        toast.warning(isAr ? `نجح ${successes}، فشل ${failures}` : `${successes} ok, ${failures} failed`);
      } else {
        toast.success(isAr ? `تم تنفيذ "${tpl.name}"` : `Executed "${tpl.name}"`);
      }
      setTimeout(() => {
        load();
        loadPositions();
        loadPending();
      }, 1000);
    } catch (err) {
      toast.error(err.response?.data?.detail || (isAr ? 'فشل التنفيذ' : 'Execute failed'));
    } finally {
      setExecutingTpl(null);
    }
  };

  // ─── Account actions ──────────────────────────────────
  const addAccount = async () => {
    if (!newAcc.label || !newAcc.login || !newAcc.password || !newAcc.server) {
      toast.error(isAr ? 'أكمل كل الحقول' : 'Fill all fields');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/accounts`, {
        label: newAcc.label,
        login: Number(newAcc.login),
        password: newAcc.password,
        server: newAcc.server,
        mode: newAcc.mode,
      }, { headers: getAuthHeaders() });
      toast.success(isAr ? 'تمت الإضافة' : 'Added');
      setNewAcc({ label: '', login: '', password: '', server: '', mode: 'demo' });
      setAddOpen(false);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || (isAr ? 'فشلت الإضافة' : 'Add failed'));
    } finally {
      setSaving(false);
    }
  };

  const deleteAccount = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/accounts/${pendingDelete.id}`, { headers: getAuthHeaders() });
      toast.success(isAr ? 'تم الحذف' : 'Deleted');
      setPendingDelete(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setDeleting(false);
    }
  };

  const disconnectAll = async () => {
    setDisconnecting(true);
    try {
      await axios.post(`${API}/disconnect-all`, {}, { headers: getAuthHeaders() });
      toast.success(isAr ? 'تم فصل الجميع' : 'All disconnected');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setDisconnecting(false);
    }
  };

  // ─── Per-account connect / disconnect (persistent toggle) ─────────
  const [togglingIds, setTogglingIds] = useState(() => new Set());

  const setToggling = (id, on) => {
    setTogglingIds((prev) => {
      const next = new Set(prev);
      if (on) next.add(id); else next.delete(id);
      return next;
    });
    // Clear any previous connect-error for this account when re-attempting
    if (on) {
      setConnectErrors((prev) => prev.filter((e) => e.account_id !== id));
    }
  };

  const connectOne = async (acc) => {
    if (!acc?.id) return;
    setToggling(acc.id, true);
    try {
      const res = await axios.post(
        `${API}/accounts/${acc.id}/connect`, {}, { headers: getAuthHeaders() },
      );
      if (res.data?.connected) {
        toast.success(isAr ? `${acc.label} متصل` : `${acc.label} connected`);
      } else {
        const err = res.data?.error || (isAr ? 'فشل الاتصال' : 'Connect failed');
        toast.error(`${acc.label}: ${err}`, { duration: 12000 });
        setConnectErrors((prev) => [
          ...prev.filter((e) => e.account_id !== acc.id),
          { account_id: acc.id, error: err },
        ]);
      }
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || (isAr ? 'فشل الاتصال' : 'Connect failed'));
    } finally {
      setToggling(acc.id, false);
    }
  };

  const disconnectOne = async (acc) => {
    if (!acc?.id) return;
    setToggling(acc.id, true);
    try {
      await axios.post(
        `${API}/accounts/${acc.id}/disconnect`, {}, { headers: getAuthHeaders() },
      );
      toast.success(isAr ? `${acc.label} تم فصله` : `${acc.label} disconnected`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setToggling(acc.id, false);
    }
  };

  // Poll live price for the selected symbol whenever the user opens
  // distance mode on either SL or TP. Poll every 2s while active.
  useEffect(() => {
    const needsLive = (order.slMode === 'distance' || order.tpMode === 'distance');
    const anyConnected = accounts.some((a) => a.connected);
    if (!needsLive || !order.symbol || !anyConnected) {
      setLivePrice(null);
      return;
    }
    let alive = true;
    const fetchPrice = async () => {
      try {
        const res = await axios.get(
          `${API}/price/${order.symbol.trim().toUpperCase()}`,
          { headers: getAuthHeaders() },
        );
        if (alive) setLivePrice({ bid: res.data.bid, ask: res.data.ask });
      } catch {
        if (alive) setLivePrice(null);
      }
    };
    fetchPrice();
    const iv = setInterval(fetchPrice, 2000);
    return () => { alive = false; clearInterval(iv); };
  }, [order.slMode, order.tpMode, order.symbol, accounts]);

  // ─── Order actions ────────────────────────────────────
  const placeOrder = async (side) => {
    if (!order.symbol || !order.volume || Number(order.volume) <= 0) {
      toast.error(isAr ? 'أدخل الرمز واللوت' : 'Enter symbol & lot');
      return;
    }
    if (connectedIds.length === 0) {
      toast.error(isAr ? 'لا يوجد حساب متصل' : 'No connected accounts');
      return;
    }

    // ── Sanity gate: refuse blatantly-wrong SL/TP that will make the broker
    //    reject the order with "Invalid stops in the request".
    const isPending = order.mode === 'pending';
    const effectiveSide = isPending
      ? (order.orderType.startsWith('buy') ? 'buy' : 'sell')
      : side;
    const base = isPending
      ? (order.entryPrice ? Number(order.entryPrice) : null)
      : (livePrice ? (effectiveSide === 'buy' ? livePrice.ask : livePrice.bid) : null);

    if (base && base > 0) {
      const slNum = order.sl ? Number(order.sl) : null;
      const tpNum = order.tp ? Number(order.tp) : null;
      const problems = [];
      if (order.slMode === 'price' && slNum && slNum > 0) {
        if (effectiveSide === 'buy' && slNum >= base) problems.push(
          isAr ? `SL يجب أن يكون أقل من ${base.toFixed(2)} لصفقة شراء` : `SL must be < ${base.toFixed(2)} on a BUY`,
        );
        if (effectiveSide === 'sell' && slNum <= base) problems.push(
          isAr ? `SL يجب أن يكون أعلى من ${base.toFixed(2)} لصفقة بيع` : `SL must be > ${base.toFixed(2)} on a SELL`,
        );
      }
      if (order.tpMode === 'price' && tpNum && tpNum > 0) {
        if (effectiveSide === 'buy' && tpNum <= base) problems.push(
          isAr ? `TP يجب أن يكون أعلى من ${base.toFixed(2)} لصفقة شراء` : `TP must be > ${base.toFixed(2)} on a BUY`,
        );
        if (effectiveSide === 'sell' && tpNum >= base) problems.push(
          isAr ? `TP يجب أن يكون أقل من ${base.toFixed(2)} لصفقة بيع` : `TP must be < ${base.toFixed(2)} on a SELL`,
        );
      }
      // Refuse "wildly off" absolute-price values (>20% away) — very likely
      // the user forgot to click the "$" toggle for distance mode.
      const tooFar = (v) => v != null && Math.abs(v - base) / base > 0.20;
      if (order.slMode === 'price' && tooFar(slNum)) problems.push(
        isAr
          ? `مسافة SL (${Math.abs(slNum - base).toFixed(2)}) أكبر من 20% من السعر — هل قصدت وضع "$"؟`
          : `SL is >20% away from base — did you mean "$" mode?`,
      );
      if (order.tpMode === 'price' && tooFar(tpNum)) problems.push(
        isAr
          ? `مسافة TP (${Math.abs(tpNum - base).toFixed(2)}) أكبر من 20% من السعر — هل قصدت وضع "$"؟`
          : `TP is >20% away from base — did you mean "$" mode?`,
      );
      if (problems.length > 0) {
        problems.forEach((m) => toast.error(m, { duration: 9000 }));
        return;
      }
    }

    setPlacing(true);
    setLastResults(null);
    const toastId = toast.loading(isAr ? 'جاري إرسال الأمر...' : 'Sending order...');

    // Switch to the destination tab RIGHT AWAY (before the response) so the
    // user's eye is already on the list where the new position/order appears.
    if (order.mode === 'pending') setSubTab('pending');
    else setSubTab('positions');

    try {
      let res;
      // Build the SL/TP portion of the payload — either absolute `sl`/`tp` OR
      // a `sl_distance`/`tp_distance` (backend computes at execution time).
      const tpsl = {};
      if (order.sl && Number(order.sl) > 0) {
        if (order.slMode === 'distance') tpsl.sl_distance = Number(order.sl);
        else tpsl.sl = Number(order.sl);
      }
      if (order.tp && Number(order.tp) > 0) {
        if (order.tpMode === 'distance') tpsl.tp_distance = Number(order.tp);
        else tpsl.tp = Number(order.tp);
      }

      // Defensive guard for INSTANT (market) + $-distance mode without a live tick.
      if (order.mode === 'market'
          && (tpsl.sl_distance != null || tpsl.tp_distance != null)
          && !livePrice) {
        toast.error(
          isAr
            ? 'انتظر ظهور السعر الحي قبل استخدام وضع "$" على أمر فوري'
            : 'Wait for live bid/ask before using "$" mode on a market order',
          { id: toastId, duration: 8000 },
        );
        setPlacing(false);
        return;
      }

      if (order.mode === 'pending') {
        if (!order.entryPrice || Number(order.entryPrice) <= 0) {
          toast.error(isAr ? 'أدخل سعر الدخول' : 'Enter entry price', { id: toastId });
          setPlacing(false);
          return;
        }
        const body = {
          symbol: order.symbol.trim().toUpperCase(),
          order_type: order.orderType,
          volume: Number(order.volume),
          entry_price: Number(order.entryPrice),
          ...tpsl,
        };
        res = await axios.post(`${API}/pending-order`, body, { headers: getAuthHeaders(), timeout: 25000 });
      } else {
        const body = {
          symbol: order.symbol.trim().toUpperCase(),
          side,
          volume: Number(order.volume),
          ...tpsl,
        };
        res = await axios.post(`${API}/market-order`, body, { headers: getAuthHeaders(), timeout: 25000 });
      }
      setLastResults({ ...res.data, mode: order.mode });
      const { successes, failures, results: rr } = res.data || {};
      if (failures > 0 && successes > 0) {
        toast.warning(
          isAr ? `نجح ${successes}، فشل ${failures}` : `${successes} succeeded, ${failures} failed`,
          { id: toastId },
        );
      } else if (failures > 0) {
        // Surface the FIRST failure reason inline.
        const firstErr = (rr || []).find((r) => !r.success)?.error || '';
        const short = firstErr ? String(firstErr).slice(0, 140) : '';
        toast.error(
          (isAr ? `فشلت كل الأوامر (${failures})` : `All orders failed (${failures})`)
            + (short ? ` — ${short}` : ''),
          { id: toastId, duration: 10000 },
        );
      } else {
        toast.success(
          isAr ? `تم إرسال ${successes} أمر` : `Sent ${successes} order(s)`,
          { id: toastId },
        );
      }
      // Fast → slow refetch cascade (250ms / 800ms / 2000ms).
      setTimeout(() => {
        load();
        if (order.mode === 'pending') loadPending();
        else loadPositions();
      }, 250);
      setTimeout(() => {
        if (order.mode === 'pending') loadPending();
        else loadPositions();
      }, 800);
      setTimeout(() => {
        loadPositions();
        loadPending();
      }, 2000);
    } catch (err) {
      // `detail` from FastAPI can be a plain string OR a nested object.
      const raw = err.response?.data?.detail;
      const msg = typeof raw === 'string'
        ? raw
        : (raw?.error?.message || raw?.message || err.message || (isAr ? 'فشل الأمر' : 'Order failed'));
      toast.error(msg, { id: toastId, duration: 8000 });
    } finally {
      setPlacing(false);
    }
  };

  const modifyTpsl = async () => {
    const slRaw = tpslForm.sl && Number(tpslForm.sl) > 0 ? Number(tpslForm.sl) : null;
    const tpRaw = tpslForm.tp && Number(tpslForm.tp) > 0 ? Number(tpslForm.tp) : null;
    if (slRaw === null && tpRaw === null) {
      toast.error(isAr ? 'أدخل SL أو TP' : 'Enter SL or TP');
      return;
    }

    // "$-distance" mode → convert to absolute price using the AVERAGE
    // open-price of matching positions.
    let sl = slRaw;
    let tp = tpRaw;
    const usesDistance = tpslForm.slMode === 'distance' || tpslForm.tpMode === 'distance';
    if (usesDistance) {
      const symFilter = tpslForm.symbol ? tpslForm.symbol.trim().toUpperCase() : '';
      const matching = [];
      (positionsData.accounts || []).forEach((accBlock) => {
        (accBlock.positions || []).forEach((p) => {
          if (!symFilter || String(p.symbol).toUpperCase() === symFilter) {
            matching.push(p);
          }
        });
      });
      if (matching.length === 0) {
        toast.error(
          isAr
            ? 'لا توجد صفقات مطابقة لحساب المسافة'
            : 'No matching positions to compute distance against',
        );
        return;
      }
      const sides = new Set(matching.map((p) => String(p.type).toLowerCase()));
      if (sides.size > 1) {
        toast.error(
          isAr
            ? 'وضع "$" يعمل فقط عندما تكون كل الصفقات من نفس الاتجاه (شراء أو بيع)'
            : '"$" mode requires all matching positions to be the same side (buy OR sell)',
        );
        return;
      }
      const isBuy = matching[0].type === 'buy';
      const avg = matching.reduce((s, p) => s + Number(p.open_price || 0), 0) / matching.length;
      if (!avg || avg <= 0) {
        toast.error(isAr ? 'تعذّر قراءة سعر الفتح' : 'Could not read open-price');
        return;
      }
      if (tpslForm.slMode === 'distance' && slRaw != null) {
        sl = Number((isBuy ? avg - slRaw : avg + slRaw).toFixed(5));
      }
      if (tpslForm.tpMode === 'distance' && tpRaw != null) {
        tp = Number((isBuy ? avg + tpRaw : avg - tpRaw).toFixed(5));
      }
    }

    setModifyingTpsl(true);
    try {
      const res = await axios.post(`${API}/modify-tpsl`, {
        symbol: tpslForm.symbol ? tpslForm.symbol.trim().toUpperCase() : null,
        sl, tp,
      }, { headers: getAuthHeaders() });
      const total = res.data?.total_modified ?? 0;
      toast.success(isAr ? `تم تعديل ${total} صفقة` : `Modified ${total} position(s)`);
      setTpslOpen(false);
      setTpslForm({ symbol: order.symbol, slMode: 'price', tpMode: 'price', sl: '', tp: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setModifyingTpsl(false);
    }
  };

  const closeAll = async () => {
    setClosingAll(true);
    // Purge tracked positions of this symbol so the poll doesn't fire chimes.
    const sym = order.symbol ? order.symbol.trim().toUpperCase() : null;
    knownPositionsRef.current.forEach((prev, key) => {
      if (!sym || prev.symbol === sym) knownPositionsRef.current.delete(key);
    });

    // Optimistic UI — strip matching positions immediately.
    setPositionsData((prev) => {
      const nextAccounts = (prev.accounts || []).map((acc) => ({
        ...acc,
        positions: (acc.positions || []).filter((p) => (sym ? p.symbol !== sym : false)),
      }));
      const remaining = nextAccounts.reduce((s, a) => s + (a.positions?.length || 0), 0);
      return { ...prev, accounts: nextAccounts, total_positions: remaining };
    });

    const toastId = toast.loading(isAr ? 'جاري إغلاق كل الصفقات...' : 'Closing all positions...');
    try {
      const res = await axios.post(`${API}/close-all`, {
        symbol: sym || undefined,
      }, { headers: getAuthHeaders(), timeout: 25000 });
      const total = res.data?.total_closed ?? 0;
      toast.success(
        isAr ? `تم إغلاق ${total} صفقة` : `Closed ${total} position(s)`,
        { id: toastId },
      );
      setTimeout(() => { load(); loadPositions(); }, 300);
      setTimeout(() => { loadPositions(); }, 1000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed', { id: toastId });
      setTimeout(loadPositions, 400);
    } finally {
      setClosingAll(false);
    }
  };

  // ─── Derived ──────────────────────────────────────────
  const connectedIds = useMemo(
    () => accounts.filter((a) => a.connected).map((a) => a.id),
    [accounts],
  );
  const totalBalance = useMemo(
    () => accounts.reduce((s, a) => s + (a.balance || 0), 0),
    [accounts],
  );
  const totalEquity = useMemo(
    () => accounts.reduce((s, a) => s + (a.equity || 0), 0),
    [accounts],
  );
  const totalProfit = useMemo(
    () => accounts.reduce((s, a) => s + (a.profit || 0), 0),
    [accounts],
  );

  // Per-account open-position counts (buy/sell + total + live P&L).
  const positionsByAccount = useMemo(() => {
    const map = {};
    (positionsData.accounts || []).forEach((accBlock) => {
      let buys = 0, sells = 0, total = 0, profit = 0;
      (accBlock.positions || []).forEach((p) => {
        total += 1;
        if (String(p.type).toLowerCase() === 'buy') buys += 1;
        else sells += 1;
        profit += Number(p.profit || 0);
      });
      map[accBlock.account_id] = { total, buys, sells, profit };
    });
    return map;
  }, [positionsData]);

  // ─── Render ───────────────────────────────────────────
  return (
    <div
      className="min-h-screen bg-[#0a0a0b] text-white"
      data-testid="manual-trading-page"
    >
      <DashboardHeader
        showBackButton
        onBack={() => navigate('/meta5-trading-hub')}
      />

      <PageBreadcrumb items={[
        { label: '606 MT5 AI AND MANUAL TRADING', to: '/meta5-trading-hub' },
        { label: '606 MT5 MANUAL TRADING' },
      ]} />

      {/* Body */}
      <div dir={isAr ? 'rtl' : 'ltr'} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-4">
        <ManualActionBar
          isAr={isAr}
          connectedCount={connectedIds.length}
          accountsCount={accounts.length}
          refreshing={refreshing}
          onRefresh={refresh}
          soundEnabled={soundEnabled}
          onToggleSound={toggleSound}
          disconnecting={disconnecting}
          onDisconnectAll={disconnectAll}
          onOpenLog={() => navigate('/meta5-trading/manual/log')}
        />

        {/* Content — stacked flow: Order Form → Stats → Templates chips → tabbed data box */}
        <div className="space-y-4">

        {/* ─── ORDER FORM — full-width, horizontal, sticky at the top ─── */}
        <OrderForm
          order={order}
          setOrder={setOrder}
          livePrice={livePrice}
          isAr={isAr}
          placeOrder={placeOrder}
          placing={placing}
          closingAll={closingAll}
          closeAll={closeAll}
          connectedIds={connectedIds}
        />

        {/* ─── STATS — 3 aggregate cards ─── */}
        <div className="grid grid-cols-3 gap-3">
          <StatCard
            label={isAr ? 'الرصيد الإجمالي' : 'Total Balance'}
            value={fmtMoney(totalBalance)}
            icon={DollarSign}
            accent="#d4af37"
            testId="stat-balance"
          />
          <StatCard
            label={isAr ? 'الإيكويتي' : 'Equity'}
            value={fmtMoney(totalEquity)}
            icon={ListChecks}
            accent="#38bdf8"
            testId="stat-equity"
          />
          <StatCard
            label={isAr ? 'الربح الحالي' : 'Live P&L'}
            value={fmtMoney(totalProfit)}
            icon={totalProfit >= 0 ? TrendingUp : TrendingDown}
            accent={totalProfit >= 0 ? '#22c55e' : '#ef4444'}
            testId="stat-pnl"
          />
        </div>

        {/* ─── QUICK TEMPLATES CHIPS ─── */}
        <TemplateChips
          templates={templates}
          isAr={isAr}
          connectedCount={connectedIds.length}
          executingId={executingTpl}
          onExecute={(t) => setConfirmExecuteTpl(t)}
        />

        {/* ─── COMBINED SECTION: Accounts / Positions / Pending / Templates ─── */}
        <TabbedSection
          testId="section-data"
          active={subTab}
          onChange={setSubTab}
          action={
            subTab === 'positions' && (positionsData.total_positions ?? 0) > 0 ? (
              <button
                onClick={() => {
                  setTpslForm({ symbol: order.symbol, slMode: 'price', tpMode: 'price', sl: '', tp: '' });
                  setTpslOpen(true);
                }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-500/15 border border-blue-500/40 text-blue-200 text-[11px] font-bold hover:bg-blue-500/25 transition"
                data-testid="positions-modify-all-tpsl"
                title={isAr ? 'تعديل الـ TP/SL لكل الصفقات المفتوحة' : 'Modify SL/TP for every open position'}
              >
                <Edit3 className="h-3.5 w-3.5" />
                {isAr ? 'تعديل TP/SL للكل' : 'Modify TP/SL (all)'}
              </button>
            ) : subTab === 'accounts' ? (
              <button
                onClick={() => setAddOpen(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#d4af37]/15 border border-[#d4af37]/40 text-[#d4af37] text-[11px] font-bold hover:bg-[#d4af37]/25 transition"
                data-testid="accounts-add"
                title={isAr ? 'إضافة حساب' : 'Add account'}
              >
                <Plus className="h-3.5 w-3.5" />
                {isAr ? 'إضافة حساب' : 'Add account'}
              </button>
            ) : null
          }
          tabs={[
            {
              id: 'accounts',
              label: isAr ? 'الحسابات' : 'Accounts',
              icon: Layers,
              badge: accounts.length,
              content: (
                <AccountsPane
                  loading={loading}
                  accounts={accounts}
                  isAr={isAr}
                  onDelete={setPendingDelete}
                  connectErrors={connectErrors}
                  onClearErrors={() => setConnectErrors([])}
                  onConnect={connectOne}
                  onDisconnect={disconnectOne}
                  togglingIds={togglingIds}
                  positionsByAccount={positionsByAccount}
                  connectedCount={connectedIds.length}
                />
              ),
            },
            {
              id: 'positions',
              label: isAr ? 'الصفقات المفتوحة' : 'Open Positions',
              icon: TrendingUp,
              badge: positionsData.total_positions,
              content: (
                <PositionsPane
                  data={positionsData}
                  accounts={accounts}
                  isAr={isAr}
                  onCloseOne={closeSinglePosition}
                  closingId={closingSinglePos}
                  onRefresh={loadPositions}
                />
              ),
            },
            {
              id: 'pending',
              label: isAr ? 'الأوامر المعلّقة' : 'Pending Orders',
              icon: Target,
              badge: pendingData.total_orders,
              content: (
                <PendingPane
                  data={pendingData}
                  accounts={accounts}
                  isAr={isAr}
                  onCancelOne={cancelSinglePending}
                  cancelingId={cancelingSingle}
                  onCancelAll={() => setConfirmCancelPending(true)}
                  cancelingAll={cancelingAll}
                  onRefresh={loadPending}
                />
              ),
            },
            {
              id: 'templates',
              label: isAr ? 'القوالب' : 'Templates',
              icon: Bookmark,
              badge: templates.length,
              content: (
                <TemplatesPane
                  templates={templates}
                  loading={templatesLoading}
                  isAr={isAr}
                  onOpenNew={() => openTplDialog(null)}
                  onEdit={(t) => openTplDialog(t)}
                  onDelete={deleteTemplate}
                  onExecute={(t) => setConfirmExecuteTpl(t)}
                  connectedCount={connectedIds.length}
                  executingId={executingTpl}
                />
              ),
            },
          ]}
        />

        {/* ─── Last order results (only when set) ─── */}
        <LastResultsCard lastResults={lastResults} accounts={accounts} isAr={isAr} />

        </div>
      </div>

      {/* ─── Dialogs ─── */}
      <AddAccountDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        isAr={isAr}
        newAcc={newAcc}
        setNewAcc={setNewAcc}
        saving={saving}
        onSave={addAccount}
      />
      <DeleteAccountDialog
        pendingDelete={pendingDelete}
        onOpenChange={setPendingDelete}
        isAr={isAr}
        deleting={deleting}
        onConfirm={deleteAccount}
      />
      <TpslModifyDialog
        open={tpslOpen}
        onOpenChange={setTpslOpen}
        isAr={isAr}
        form={tpslForm}
        setForm={setTpslForm}
        modifying={modifyingTpsl}
        onApply={modifyTpsl}
      />
      <CancelAllPendingDialog
        open={confirmCancelPending}
        onOpenChange={setConfirmCancelPending}
        isAr={isAr}
        onConfirm={cancelAllPending}
      />
      <ExecuteTemplateDialog
        tpl={confirmExecuteTpl}
        onOpenChange={setConfirmExecuteTpl}
        isAr={isAr}
        connectedCount={connectedIds.length}
        onExecute={executeTemplate}
      />
      <TemplateEditorDialog
        open={tplOpen}
        onOpenChange={setTplOpen}
        isAr={isAr}
        form={tplForm}
        setForm={setTplForm}
        editing={editingTpl}
        saving={savingTpl}
        onSave={saveTemplate}
      />
    </div>
  );
}
